import React, { useEffect, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import "../../css/createSourceModal.css";
import {
    bigqueryDestinationAuthorize,
    createBigqueryDataset,
    createBigqueryTable,
    createDestinationBigquery,
    getBigqueryDestinationTables,
    getBigqueryDestinationUrl,
} from "../../helpers/destinations/bigquery/bigqueryDestination";
import { addDestinationToTracker } from "../../helpers/destinations/createDestination";
import LoginButton from "../molecules/LoginButton";
import { ConfirmationModal } from "../organisms/ConfirmationModal";
import SwalAlertComp from "../SwalAlert";

const BIGQUERY_NAME_REGEX = /^[a-zA-Z0-9_]+$/;
const CREATE_NEW_DATASET_VALUE = "__CREATE_NEW_DATASET__";
const CREATE_NEW_TABLE_VALUE = "__CREATE_NEW_TABLE__";

const SCHEMA_MISMATCH_MESSAGE =
    "The existing table schema does not match SignalSight requirements. Continuing will update the table schema. Are you sure you want to proceed?";
const TABLE_NOT_EMPTY_MESSAGE =
    "The target table is not empty. If it is being used by another data source, this may cause data conflicts or inconsistencies. Are you sure you want to proceed?";
const COMBINED_WARNING_MESSAGE =
    "The target table is not empty and its schema does not match SignalSight requirements. Continuing may cause data conflicts and will update the table schema. Are you sure you want to proceed?";

const getConfirmationMessage = (confirmationData) => {
    if (confirmationData?.message) {
        return confirmationData.message;
    }

    const warnings = confirmationData?.warnings || [];
    const hasSchemaMismatch = warnings.includes("schema_mismatch");
    const hasTableNotEmpty = warnings.includes("table_not_empty");

    if (hasSchemaMismatch && hasTableNotEmpty) {
        return COMBINED_WARNING_MESSAGE;
    }
    if (hasSchemaMismatch) {
        return SCHEMA_MISMATCH_MESSAGE;
    }
    if (hasTableNotEmpty) {
        return TABLE_NOT_EMPTY_MESSAGE;
    }

    return COMBINED_WARNING_MESSAGE;
};

const BigqueryDestination = ({
    setSelectedDestination,
    getAllDestinationsRedux,
    setIsLoading,
    language,
    tracker_id,
    getTracker,
    handleCloseModal,
    onDestinationCreated,
    registerFooterAction,
}) => {
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [bigqueryUrl, setBigqueryUrl] = useState("");
    const [bigqueryProgressStep, setProgressStep] = useState(0);
    const [bigqueryInstallId, setBigqueryInstallId] = useState();
    const [bigqueryProjects, setBigqueryProjects] = useState([]);
    const [bigqueryTables, setBigqueryTables] = useState([]);
    const [bigqueryDatasets, setBigqueryDatasets] = useState([]);
    const [bigquerySelectedProjectId, setBigquerySelectedProjectId] =
        useState(null);
    const [bigquerySelectedDataset, setBigquerySelectedDataset] =
        useState(null);
    const [bigquerySelectedTable, setBigquerySelectedTable] = useState(null);

    // Create new dataset/table states
    const [isCreatingNewDataset, setIsCreatingNewDataset] = useState(false);
    const [isCreatingNewTable, setIsCreatingNewTable] = useState(false);
    const [newDatasetName, setNewDatasetName] = useState("");
    const [newTableName, setNewTableName] = useState("");
    const [datasetError, setDatasetError] = useState("");
    const [tableError, setTableError] = useState("");

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [pendingPayload, setPendingPayload] = useState(null);
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    const initBigquery = async () => {
        setIsLoadingButton(true);
        let response = await getBigqueryDestinationUrl();
        setBigqueryUrl(response?.data?.auth_url);
        setIsLoadingButton(false);
    };

    useEffect(() => {
        initBigquery();
    }, []);

    const sendBigqueryData = async (data) => {
        setIsLoadingButton(true);

        let newData = {
            code: data?.code,
        };
        let response = await bigqueryDestinationAuthorize(newData);
        if (response.status) {
            setBigqueryInstallId(response?.data?.install_id);
            setBigqueryProjects(response?.data?.projects || []);
            setProgressStep(1);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoadingButton(false);
    };

    const handleProjectIdChange = (event, selectedOpt) => {
        if (selectedOpt?.project_id) {
            setBigquerySelectedProjectId(selectedOpt.project_id);
            let datasets =
                bigqueryProjects.find(
                    (x) => x.project_id === selectedOpt.project_id,
                )?.datasets || [];
            setBigqueryDatasets(datasets.map((x) => ({ value: x, label: x })));
            setBigquerySelectedDataset(null);
            setBigquerySelectedTable(null);
            setBigqueryTables([]);
            setIsCreatingNewDataset(false);
            setIsCreatingNewTable(false);
        } else {
            // Clear all when x is clicked
            setBigquerySelectedProjectId(null);
            setBigqueryDatasets([]);
            setBigquerySelectedDataset(null);
            setBigquerySelectedTable(null);
            setBigqueryTables([]);
            setIsCreatingNewDataset(false);
            setIsCreatingNewTable(false);
            setNewDatasetName("");
            setNewTableName("");
            setDatasetError("");
            setTableError("");
        }
    };

    const handleDatasetChange = (event, selectedOpt) => {
        if (selectedOpt?.value === CREATE_NEW_DATASET_VALUE) {
            setIsCreatingNewDataset(true);
            setBigquerySelectedDataset(null);
            setNewDatasetName("");
            setDatasetError("");
            setBigquerySelectedTable(null);
            setBigqueryTables([]);
            setIsCreatingNewTable(false);
            setNewTableName("");
            setTableError("");
        } else if (selectedOpt?.value) {
            setIsCreatingNewDataset(false);
            setBigquerySelectedDataset(selectedOpt.value);
            setNewDatasetName("");
            setDatasetError("");
            setBigquerySelectedTable(null);
            setIsCreatingNewTable(false);
            setNewTableName("");
            setTableError("");
            // Fetch tables for selected dataset
            fetchTablesForDataset(selectedOpt.value);
        } else {
            // Clear when x is clicked
            setIsCreatingNewDataset(false);
            setBigquerySelectedDataset(null);
            setNewDatasetName("");
            setDatasetError("");
            setBigquerySelectedTable(null);
            setBigqueryTables([]);
            setIsCreatingNewTable(false);
            setNewTableName("");
            setTableError("");
        }
    };

    const fetchTablesForDataset = async (dataset) => {
        setIsLoadingButton(true);
        let response = await getBigqueryDestinationTables(
            bigquerySelectedProjectId,
            dataset,
            bigqueryInstallId,
        );
        if (response.status) {
            setBigqueryTables(
                response?.data?.tables?.map((x) => ({
                    value: x,
                    label: x,
                })) || [],
            );
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoadingButton(false);
    };

    const handleTableChange = (event, selectedOpt) => {
        if (selectedOpt?.value === CREATE_NEW_TABLE_VALUE) {
            setIsCreatingNewTable(true);
            setBigquerySelectedTable(null);
            setNewTableName("");
            setTableError("");
        } else if (selectedOpt?.value) {
            setIsCreatingNewTable(false);
            setBigquerySelectedTable(selectedOpt.value);
            setNewTableName("");
            setTableError("");
        } else {
            // Clear when x is clicked
            setIsCreatingNewTable(false);
            setBigquerySelectedTable(null);
            setNewTableName("");
            setTableError("");
        }
    };

    const handleNewDatasetNameChange = (event) => {
        const value = event.target.value;
        setNewDatasetName(value);
        if (value && !BIGQUERY_NAME_REGEX.test(value)) {
            setDatasetError(
                "The dataset must contain only letters, numbers, and underscores.",
            );
        } else {
            setDatasetError("");
        }
    };

    const handleNewTableNameChange = (event) => {
        const value = event.target.value;
        setNewTableName(value);
        if (value && !BIGQUERY_NAME_REGEX.test(value)) {
            setTableError(
                "The table must contain only letters, numbers, and underscores.",
            );
        } else {
            setTableError("");
        }
    };

    const handleCreateDataset = async () => {
        if (!newDatasetName) {
            setDatasetError("Dataset name is required");
            return;
        }
        if (!BIGQUERY_NAME_REGEX.test(newDatasetName)) {
            setDatasetError(
                "The dataset must contain only letters, numbers, and underscores.",
            );
            return;
        }

        setIsLoadingButton(true);
        const response = await createBigqueryDataset({
            install_id: bigqueryInstallId,
            project: bigquerySelectedProjectId,
            dataset: newDatasetName,
        });

        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response?.message ?? "Dataset created successfully",
                icon: "success",
                timer: 2000,
            });
            // Add to datasets list and select it
            setBigqueryDatasets([
                ...bigqueryDatasets,
                { value: newDatasetName, label: newDatasetName },
            ]);
            setBigquerySelectedDataset(newDatasetName);
            setIsCreatingNewDataset(false);

            // When creating a dataset, backend automatically creates a table with the same name
            // Add it to tables list and select it automatically
            setBigqueryTables([
                { value: newDatasetName, label: newDatasetName },
            ]);
            setBigquerySelectedTable(newDatasetName);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "Failed to create dataset",
                icon: "error",
            });
            if (response.errors) {
                setDatasetError(
                    response.errors.dataset?.[0] || response.message,
                );
            }
        }
        setIsLoadingButton(false);
    };

    const handleCreateTable = async () => {
        if (!newTableName) {
            setTableError("Table name is required");
            return;
        }
        if (!BIGQUERY_NAME_REGEX.test(newTableName)) {
            setTableError(
                "The table must contain only letters, numbers, and underscores.",
            );
            return;
        }

        setIsLoadingButton(true);
        const response = await createBigqueryTable({
            install_id: bigqueryInstallId,
            project: bigquerySelectedProjectId,
            dataset: isCreatingNewDataset
                ? newDatasetName
                : bigquerySelectedDataset,
            table: newTableName,
        });

        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response?.message ?? "Table created successfully",
                icon: "success",
                timer: 2000,
            });
            // Add to tables list and select it
            setBigqueryTables([
                ...bigqueryTables,
                { value: newTableName, label: newTableName },
            ]);
            setBigquerySelectedTable(newTableName);
            setIsCreatingNewTable(false);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "Failed to create table",
                icon: "error",
            });
            if (response.errors) {
                setTableError(response.errors.table?.[0] || response.message);
            }
        }
        setIsLoadingButton(false);
    };

    const processSuccessfulDestination = async (response) => {
        const destinationId = response?.data?.[0]?.id;

        if (tracker_id && destinationId) {
            const newDestinationId = {
                destinations: [destinationId],
            };

            let addResponse = await addDestinationToTracker(
                newDestinationId,
                tracker_id,
            );

            if (addResponse.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message:
                        response?.message ?? "Destination created successfully",
                    icon: "success",
                    timer: 2000,
                });

                if (handleCloseModal) {
                    handleCloseModal();
                }

                if (getTracker) {
                    getTracker();
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        addResponse?.message ??
                        "Failed to add destination to tracker",
                    icon: "error",
                });
            }
        } else {
            if (destinationId && setSelectedDestination) {
                setSelectedDestination((prev) => {
                    return [...prev, destinationId];
                });
            }

            if (getAllDestinationsRedux) {
                getAllDestinationsRedux();
            }

            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message:
                    response?.message ?? "Destination created successfully",
                icon: "success",
                timer: 2000,
            });

            setProgressStep(0);
            setBigquerySelectedProjectId(null);
            setBigquerySelectedDataset(null);
            setBigquerySelectedTable(null);
            setIsCreatingNewDataset(false);
            setIsCreatingNewTable(false);
            setBigqueryInstallId(null);

            if (onDestinationCreated && destinationId) {
                await onDestinationCreated(destinationId);
            }
        }
    };

    const handleSave = async () => {
        setIsLoadingButton(true);
        setIsLoading && setIsLoading(true);

        const finalDataset = isCreatingNewDataset
            ? newDatasetName
            : bigquerySelectedDataset;
        const finalTable = isCreatingNewTable
            ? newTableName
            : bigquerySelectedTable;

        let data = {
            install_id: bigqueryInstallId,
            project: bigquerySelectedProjectId,
            dataset: finalDataset,
            table: finalTable,
        };

        let response = await createDestinationBigquery(data);
        if (response.status) {
            const confirmationData = response?.data;
            if (confirmationData?.requires_confirmation === true) {
                setPendingPayload(data);
                setConfirmMessage(getConfirmationMessage(confirmationData));
                setConfirmOpen(true);
                setIsLoadingButton(false);
                setIsLoading && setIsLoading(false);
                return;
            }

            await processSuccessfulDestination(response);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoadingButton(false);
        setIsLoading && setIsLoading(false);
    };

    const handleConfirmCancel = () => {
        if (isConfirmLoading) return;
        setConfirmOpen(false);
        setPendingPayload(null);
        setConfirmMessage("");
    };

    const handleConfirmContinue = async () => {
        if (!pendingPayload) return;

        setIsConfirmLoading(true);
        setIsLoadingButton(true);
        setIsLoading && setIsLoading(true);

        const response = await createDestinationBigquery({
            ...pendingPayload,
            confirmed: true,
        });

        setConfirmOpen(false);
        setPendingPayload(null);
        setConfirmMessage("");
        setIsConfirmLoading(false);

        if (response.status) {
            await processSuccessfulDestination(response);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message ?? "",
                icon: "error",
            });
        }

        setIsLoadingButton(false);
        setIsLoading && setIsLoading(false);
    };

    const hasProject = !!bigquerySelectedProjectId;
    const hasDataset =
        !!bigquerySelectedDataset ||
        (isCreatingNewDataset && !!newDatasetName && !datasetError);
    // A new dataset creates its table with it, so a table is only required
    // once an existing dataset is selected.
    const hasTable =
        !bigquerySelectedDataset || isCreatingNewDataset
            ? true
            : isCreatingNewTable
              ? !!newTableName && !tableError
              : !!bigquerySelectedTable;
    const canCreate = hasProject && hasDataset && hasTable;

    useHostFooterAction(registerFooterAction, "form", {
        label: language?.buttons?.create_destination ?? "Create destination",
        onSubmit: handleSave,
        isDisabled: !canCreate || confirmOpen,
        isLoading: isLoadingButton,
        hidden: bigqueryProgressStep !== 1,
    });

    return (
        <div>
            <ConfirmationModal
                open={confirmOpen}
                onClose={handleConfirmCancel}
                onConfirm={handleConfirmContinue}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message={confirmMessage}
                confirmText="Continue"
                cancelText={language?.delete_swal_modal?.no ?? "Cancel"}
                confirmColor="warning"
                isLoading={isConfirmLoading}
            />
            {bigqueryProgressStep === 0 && (
                <div>
                    <LoginButton
                        code="bigquery"
                        url={bigqueryUrl}
                        paramNames={["code"]}
                        onSuccess={sendBigqueryData}
                        onError={() => {
                            setIsLoadingButton(false);
                        }}
                        dataTransform={(params) => ({ code: params.code })}
                        logoSrc="/assets/images/bigquery.png"
                        buttonText={
                            language?.buttons?.connect_with_bigquery ??
                            "Connect with Google BigQuery"
                        }
                        disabled={isLoadingButton}
                        isLoadingExternal={isLoadingButton}
                    />
                </div>
            )}
            {bigqueryProgressStep === 1 && (
                <div>
                    {/* Project */}
                    <div className="mb-3">
                        <FormControl fullWidth>
                            <Autocomplete
                                name="project_id"
                                onChange={handleProjectIdChange}
                                value={
                                    bigqueryProjects.find(
                                        (p) =>
                                            p.project_id ===
                                            bigquerySelectedProjectId,
                                    ) || null
                                }
                                options={bigqueryProjects}
                                getOptionLabel={(option) =>
                                    option.project_name || ""
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            language?.destination_modal
                                                ?.project ?? "Project"
                                        }
                                        placeholder="Search for a project..."
                                        variant="outlined"
                                    />
                                )}
                            />
                        </FormControl>
                    </div>

                    {/* Dataset and Table */}
                    {bigquerySelectedProjectId && (
                        <div>
                            {/* Dataset */}
                            <div className="mb-3">
                                <FormControl fullWidth className="mb-2">
                                    <Autocomplete
                                        name="dataset"
                                        onChange={handleDatasetChange}
                                        value={
                                            isCreatingNewDataset
                                                ? {
                                                      value: CREATE_NEW_DATASET_VALUE,
                                                      label: "+ Create new dataset",
                                                  }
                                                : bigqueryDatasets.find(
                                                      (d) =>
                                                          d.value ===
                                                          bigquerySelectedDataset,
                                                  ) || null
                                        }
                                        options={[
                                            {
                                                value: CREATE_NEW_DATASET_VALUE,
                                                label: "+ Create new dataset",
                                            },
                                            ...bigqueryDatasets,
                                        ]}
                                        getOptionLabel={(option) =>
                                            option.label || ""
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={
                                                    language?.destination_modal
                                                        ?.dataset ?? "Dataset"
                                                }
                                                placeholder="Search for a dataset..."
                                                variant="outlined"
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li
                                                {...props}
                                                style={
                                                    option.value ===
                                                    CREATE_NEW_DATASET_VALUE
                                                        ? {
                                                              color: "#1976d2",
                                                              fontWeight: 500,
                                                          }
                                                        : {}
                                                }
                                            >
                                                {option.label}
                                            </li>
                                        )}
                                    />
                                </FormControl>

                                {isCreatingNewDataset && (
                                    <div>
                                        <FormControl
                                            fullWidth
                                            className="mb-2"
                                            error={!!datasetError}
                                        >
                                            <TextField
                                                label={
                                                    language?.destination_modal
                                                        ?.dataset_name ??
                                                    "Dataset Name *"
                                                }
                                                placeholder="e.g., analytics_data"
                                                value={newDatasetName}
                                                onChange={
                                                    handleNewDatasetNameChange
                                                }
                                                error={!!datasetError}
                                                variant="outlined"
                                            />
                                            {datasetError && (
                                                <FormHelperText>
                                                    {datasetError}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleCreateDataset}
                                            disabled={
                                                !newDatasetName ||
                                                !!datasetError ||
                                                isLoadingButton
                                            }
                                            size="small"
                                        >
                                            {language?.buttons
                                                ?.create_dataset ??
                                                "Create Dataset"}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Table - Only show if a dataset is selected (not while creating) */}
                            {bigquerySelectedDataset && (
                                <div className="mb-3">
                                    <FormControl fullWidth className="mb-2">
                                        <Autocomplete
                                            name="table"
                                            onChange={handleTableChange}
                                            value={
                                                isCreatingNewTable
                                                    ? {
                                                          value: CREATE_NEW_TABLE_VALUE,
                                                          label: "+ Create new table",
                                                      }
                                                    : bigqueryTables.find(
                                                          (t) =>
                                                              t.value ===
                                                              bigquerySelectedTable,
                                                      ) || null
                                            }
                                            options={[
                                                {
                                                    value: CREATE_NEW_TABLE_VALUE,
                                                    label: "+ Create new table",
                                                },
                                                ...bigqueryTables,
                                            ]}
                                            getOptionLabel={(option) =>
                                                option.label || ""
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        language
                                                            ?.destination_modal
                                                            ?.table ?? "Table"
                                                    }
                                                    placeholder="Search for a table..."
                                                    variant="outlined"
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={
                                                        option.value ===
                                                        CREATE_NEW_TABLE_VALUE
                                                            ? {
                                                                  color: "#1976d2",
                                                                  fontWeight: 500,
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    {option.label}
                                                </li>
                                            )}
                                        />
                                    </FormControl>

                                    {isCreatingNewTable && (
                                        <div>
                                            <FormControl
                                                fullWidth
                                                className="mb-2"
                                                error={!!tableError}
                                            >
                                                <TextField
                                                    label={
                                                        language
                                                            ?.destination_modal
                                                            ?.table_name ??
                                                        "Table Name *"
                                                    }
                                                    placeholder="e.g., user_events"
                                                    value={newTableName}
                                                    onChange={
                                                        handleNewTableNameChange
                                                    }
                                                    error={!!tableError}
                                                    variant="outlined"
                                                />
                                                {tableError && (
                                                    <FormHelperText>
                                                        {tableError}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={handleCreateTable}
                                                disabled={
                                                    !newTableName ||
                                                    !!tableError ||
                                                    isLoadingButton
                                                }
                                                size="small"
                                            >
                                                {language?.buttons
                                                    ?.create_table ??
                                                    "Create Table"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BigqueryDestination;
